import { Box, Stack, Typography } from '@mui/material';
import { BetWithMarketOutcomeEvent, StyleObj } from '../../../../@types';
import { getReofferedColor } from '../../../../utils';
import { BetDetails } from '../../../atoms/BetDetails';
import OddsDisplay from '../../../atoms/OddsDisplay';
import { useMarketplaceConfig } from '../../../../queries/marketplace';

import { mergeSx } from '../../../../utils/styles';

const styles: StyleObj = {
  container: {
    backgroundColor: (theme) => theme.palette.neutral[100],
    borderTop: '1px solid',
    borderColor: 'neutral.200',
  },
  contentWrapper: {
    width: '100%',
    px: 1,
    py: 0.75,
  },
  header: {
    textAlign: 'left',
    '& p': { fontSize: 15 },
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  previousValue: {
    textDecoration: 'line-through',
    opacity: 0.5,
  },
};

type GolaReofferedBetslipItemProps = {
  bets: BetWithMarketOutcomeEvent[];
  previousBetsMap: Record<string, BetWithMarketOutcomeEvent>;
};

const GolaReofferedBetslipItem = ({ bets, previousBetsMap }: GolaReofferedBetslipItemProps) => {
  const { isFTBFeed } = useMarketplaceConfig();

  if (!bets || bets.length === 0) return null;

  const eventName = bets[0].event.name;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.contentWrapper}>
        <Box sx={styles.header}>
          <Typography
            sx={{
              color: 'neutral.800',
            }}
          >
            {eventName}
          </Typography>
        </Box>

        <Stack>
          {bets.map((bet) => {
            const previousBet = previousBetsMap[bet.outcomeId];

            const { color: oddsColor, changed: oddsChanged } = getReofferedColor(bet.odds, previousBet?.odds);
            const { color: specialValuesColor, changed: specialValuesChanged } = getReofferedColor(
              isFTBFeed ? bet.outcome.properties?.specialValue : bet.specialValues,
              isFTBFeed ? previousBet?.outcome.properties?.specialValue : previousBet?.specialValues
            );

            const showPreviousOutcome = isFTBFeed && specialValuesChanged && previousBet;

            return (
              <Box key={bet.outcomeId} sx={styles.row}>
                <Stack
                  direction='row'
                  spacing={0.25}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant='body3'
                    sx={{
                      color: 'neutral.600',
                    }}
                  >
                    <BetDetails.Market
                      marketName={bet.market.name}
                      marketShortName={bet.market.shortName}
                      marketType={bet.market.marketType.name}
                      color='inherit'
                      variant='inherit'
                    />
                    {' ('}
                    {showPreviousOutcome && (
                      <BetDetails.Outcome
                        outcomeName={previousBet.outcome.name}
                        outcomeShortName={previousBet.outcome.shortName}
                        {...previousBet.outcome.properties}
                        marketName={previousBet.market.name}
                        marketTypeName={previousBet.market.marketType.name}
                        marketDisplayType={previousBet.market.displayType}
                        eventName={previousBet.event.name}
                        color='neutral.600'
                        sx={styles.previousValue}
                      />
                    )}
                    <BetDetails.Outcome
                      outcomeName={bet.outcome.name}
                      outcomeShortName={bet.outcome.shortName}
                      {...bet.outcome.properties}
                      marketName={bet.market.name}
                      marketTypeName={bet.market.marketType.name}
                      marketDisplayType={bet.market.displayType}
                      eventName={bet.event.name}
                      color={isFTBFeed ? specialValuesColor : undefined}
                    />
                    {!!bet.outcome.shortName && (
                      <>
                        {specialValuesChanged && previousBet && (
                          <BetDetails.SpecialValues
                            outcomeName={previousBet.outcome.name}
                            specialValues={previousBet.specialValues}
                            marketTypeName={previousBet.market.marketType.name}
                            specialValueModel={previousBet.specialValues?.[0]?.model}
                            sportId={previousBet.market.marketType.sportId}
                            color='neutral.600'
                            sx={styles.previousValue}
                          />
                        )}
                        <BetDetails.SpecialValues
                          outcomeName={bet.outcome.name}
                          specialValues={bet.specialValues}
                          marketTypeName={bet.market.marketType.name}
                          specialValueModel={bet.specialValues?.[0]?.model}
                          sportId={bet.market.marketType.sportId}
                          color={specialValuesColor}
                        />
                      </>
                    )}
                    {')'}
                  </Typography>
                </Stack>

                <Stack
                  direction='row'
                  spacing={0.5}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  {oddsChanged && previousBet && (
                    <OddsDisplay
                      value={previousBet.odds}
                      variant='body2'
                      sx={mergeSx(
                        {
                          color: 'neutral.700',
                          px: 0.75,
                          height: 'fit-content',
                        },
                        styles.previousValue
                      )}
                    />
                  )}
                  <OddsDisplay
                    value={bet.odds}
                    variant='body2'
                    sx={{
                      color: oddsColor,
                      px: 0.75,
                      height: 'fit-content',
                    }}
                  />
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};

export default GolaReofferedBetslipItem;
